import type { StoreCategoryRef } from '../../../shared/models/blog.model';
import type { Product } from '../../../shared/models/product.model';
import { resolvePdpCategorySlug } from '../../catalog/data/catalog-mappers';

/**
 * Enlaces del blog hacia la tienda y reglas de pintado de los productos del
 * post (B68, B70, B71 del spec del blog). Todo puro: las plantillas reciben
 * vistas ya resueltas y no deciden nada.
 */

/**
 * Enlace de «Explorar en tienda» (B71, P3). Una categoría de tienda es UN
 * segmento (`/tienda/:category`); un slug con barra caería en la ruta de ficha
 * de producto (`/tienda/:category/:productSlug`) y daría «no encontrado».
 * Ante un slug vacío o con `/`, mejor sin enlace que un 404: `null`.
 */
export function storeCategoryLink(slug: string | null | undefined): string[] | null {
  const clean = slug?.trim();
  if (!clean || clean.includes('/')) return null;
  return ['/tienda', clean];
}

/**
 * Enlace a la ficha `/tienda/:category/:slug`, solo si el producto es
 * navegable (mismo criterio que la landing de juego: sin `slug` o sin
 * categoría resoluble no se enlaza, para no llevar a un 404).
 */
export function blogProductLink(product: Product): string[] | null {
  if (!product.slug) return null;
  const category = resolvePdpCategorySlug(product);
  return category ? ['/tienda', category, product.slug] : null;
}

/** Hay stock real o es precompra: espejo de `isWebAvailable()` de la API. */
function isWebAvailable(product: Product): boolean {
  return Number(product.stock) >= 1 || product.isPreSale === true;
}

/** Vista de un producto del post (relacionado o inline), ya decidida. */
export interface BlogProductView {
  product: Product;
  /** Comandos de `routerLink` a la ficha, o `null` si no es navegable. */
  link: string[] | null;
  /**
   * Se pinta el precio: la API lo enmascara en los no disponibles
   * (`hidePriceForUnavailable`), así que hace falta precio y disponibilidad.
   */
  showPrice: boolean;
  /** Sin stock y sin precompra: se pinta «Agotado» en lugar del precio. */
  soldOut: boolean;
}

export function toBlogProductView(product: Product): BlogProductView {
  const available = isWebAvailable(product);
  return {
    product,
    link: blogProductLink(product),
    showPrice: available && product.price != null,
    soldOut: !available,
  };
}

/** Píldora de «Explorar en tienda» con su enlace ya resuelto. */
export interface StoreCategoryView {
  category: StoreCategoryRef;
  link: string[];
}

/** Las categorías sin enlace válido no se pintan (B71). */
export function toStoreCategoryViews(
  categories: readonly StoreCategoryRef[] | null | undefined,
): StoreCategoryView[] {
  const views: StoreCategoryView[] = [];
  for (const category of categories ?? []) {
    const link = storeCategoryLink(category.slug);
    if (link) views.push({ category, link });
  }
  return views;
}
